import { createSelector } from '@reduxjs/toolkit';

import type { AppState } from '../../../store/types';
import {
  selectIsNaturalSearchActive,
  selectNaturalSearchIsLoading,
} from '../../natural_search/selectors';
import { selectLastNavigationItemSelectedIds } from '../../navigation_history/selectors';
import { selectIsNltfActive, selectNltfIsLoading } from '../../nltf/selectors';
import { selectIsSearchPagedByNumber } from './pagination';

/**
 * Whether one of the AI search flows is the thing filling the grid, so the
 * regular search must not be run for it: NLTF re-runs the UI search once it has
 * parsed the query, and a natural search's results would be replaced by it.
 */
export const selectIsAiSearchFillingGrid = createSelector(
  selectIsNltfActive,
  selectNltfIsLoading,
  selectIsNaturalSearchActive,
  (isNltfActive, nltfIsLoading, isNaturalSearchActive) =>
    isNaturalSearchActive || (isNltfActive && nltfIsLoading),
);

/**
 * Whether the results for the current search target are still unknown, either
 * because no fetch has settled for it yet or because one is in flight.
 *
 * `fetchStatus` alone is not enough: it is 'idle' both before the first fetch
 * starts and after a superseded chain gives up, and an empty id list in either
 * of those windows would render "no results" for a target that was never
 * fetched.
 */
export const selectIsSearchResultsPending = createSelector(
  (state: AppState) => state.pages.search.list,
  selectIsNltfActive,
  selectNltfIsLoading,
  selectIsNaturalSearchActive,
  selectNaturalSearchIsLoading,
  (
    { status, fetchStatus },
    isNltfActive,
    nltfIsLoading,
    isNaturalSearchActive,
    naturalSearchIsLoading,
  ) =>
    status === 'pending' ||
    fetchStatus === 'fetching' ||
    (isNltfActive && nltfIsLoading) ||
    (isNaturalSearchActive && naturalSearchIsLoading),
);

/**
 * Whether the list as a whole is being loaded, either for the first time or to
 * replace what is on screen after the query changed. Only this state may
 * replace the whole grid with a loading bar.
 */
export const selectIsSearchListLoading = createSelector(
  selectIsSearchPagedByNumber,
  (state: AppState) => state.pages.search.list.ids.length,
  selectIsSearchResultsPending,
  (isPagedByNumber, idsCount, isResultsPending) =>
    isResultsPending && (isPagedByNumber || idsCount === 0),
);

/**
 * Whether a batch is being loaded under results already on screen, for the
 * loading bar below the list. Covers both the scroll sentinel and a refresh
 * that keeps the current results up while it runs.
 */
export const selectIsLoadingMoreSearchItems = createSelector(
  selectIsSearchPagedByNumber,
  (state: AppState) => state.pages.search.list.ids.length,
  (state: AppState) => state.pages.search.list.fetchStatus,
  (isPagedByNumber, idsCount, fetchStatus) =>
    !isPagedByNumber && fetchStatus === 'fetching' && idsCount > 0,
);

export const selectCanResumeSearchList = createSelector(
  selectIsSearchPagedByNumber,
  (state: AppState) => state.pages.search.list.ids.length,
  selectLastNavigationItemSelectedIds,
  (isPagedByNumber, idsCount, selectedIds) =>
    !isPagedByNumber && idsCount > 0 && selectedIds.length > 0,
);
